import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../AppIcon';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { useUserChatNotifications } from '../../hooks/useChatNotifications';
import { translateMessage, detectLanguage } from '../../utils/translateMessage';
import { useLanguage } from '../../context/LanguageContext';

// ── Utility: get visitor IP ────────────────────────────────
const getVisitorIP = async () => {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip || 'Unknown';
  } catch {
    return 'Unknown';
  }
};

// ── Utility: get country code from IP ─────────────────────
const getCountryFromIP = async (ip) => {
  if (!ip || ip === 'Unknown') return null;
  try {
    const res = await fetch(`https://ipwho.is/${ip}`);
    const data = await res.json();
    return data.country_code || null; // "US", "NG", "GB", etc.
  } catch {
    return null;
  }
};

// ── Guest session persistence key ──────────────────────────
const GUEST_SESSION_KEY = 'av_guest_chat_session';

const LiveChat = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newMsgFlash, setNewMsgFlash] = useState(false);
  const [guestUnreadCount, setGuestUnreadCount] = useState(0);
  const [guestLatestMsg, setGuestLatestMsg] = useState(null);
  const isVisibleRef = useRef(false); // true only when widget is open AND not minimized
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Image upload state ────────────────────────────────
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ── Translation state ─────────────────────────────────
  const [translations, setTranslations] = useState({});
  const [userLang, setUserLang] = useState(null); // detected language of the user (e.g. "fr", "es")

  // ── Guest pre-chat form state ───────────────────────────
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestContact, setGuestContact] = useState(''); // email or phone
  const [guestMessage, setGuestMessage] = useState('');
  const [guestFormError, setGuestFormError] = useState('');
  const [submittingGuest, setSubmittingGuest] = useState(false);

  // ── Real-time unread tracking (only for authenticated users) ──
  const {
    unreadCount,
    latestMessage,
    markAllRead,
  } = useUserChatNotifications(isAuthenticated ? user?.id : null);

  // Flash animation when a new message comes in while chat is closed (authenticated)
  useEffect(() => {
    if (latestMessage && !isOpen) {
      setNewMsgFlash(true);
      const timer = setTimeout(() => setNewMsgFlash(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [latestMessage, isOpen]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ── Restore guest session from sessionStorage ───────────
  useEffect(() => {
    if (!isAuthenticated) {
      const stored = sessionStorage.getItem(GUEST_SESSION_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setChatSession(parsed);
        } catch { /* ignore */ }
      }
    }
  }, [isAuthenticated]);

  // ── Load session for authenticated users ────────────────
  const loadChatSession = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: existing } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        setChatSession(existing);
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', existing.id)
          .order('created_at', { ascending: true });

        if (msgs) setMessages(msgs);
      } else {
        // Fetch IP + country for authenticated users too
        const ip = await getVisitorIP();
        const countryCode = await getCountryFromIP(ip);

        const { data: newSession, error } = await supabase
          .from('chat_sessions')
          .insert({
            user_id: user.id,
            user_name: user.name || user.email,
            user_email: user.email,
            is_guest: false,
            guest_ip: ip,
            country_code: countryCode,
            page_url: window.location.pathname,
            status: 'open',
            subject: 'General Support',
          })
          .select()
          .single();

        if (!error && newSession) {
          setChatSession(newSession);
          const { data: welcomeMsg } = await supabase
            .from('chat_messages')
            .insert({
              session_id: newSession.id,
              sender_id: 'system',
              sender_name: 'Acebarter Support',
              sender_role: 'admin',
              message: `Hello ${user.name || 'there'}! Welcome to Acebarter support. How can we help you today?`,
              type: 'text',
            })
            .select()
            .single();

          if (welcomeMsg) setMessages([welcomeMsg]);
        }
      }
    } catch (err) {
      console.error('Chat session error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ── Load messages for a restored guest session ──────────
  const loadGuestMessages = useCallback(async (session) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      const { data: msgs } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', session.id)
        .order('created_at', { ascending: true });

      if (msgs) setMessages(msgs);
    } catch (err) {
      console.error('Failed to load guest messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Subscribe to real-time messages for the active session
  useEffect(() => {
    if (!chatSession?.id) return;

    const channel = supabase
      .channel(`chat-${chatSession.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${chatSession.id}`,
        },
        async (payload) => {
          const newMsg = payload.new;

          // Always add message to state immediately
          setMessages(prev => {
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          // If it's an admin message
          if (newMsg.sender_role === 'admin' || newMsg.sender_role === 'system') {
            if (isVisibleRef.current) {
              // Widget is visible — mark as read immediately so badge doesn't flash
              if (newMsg.read_by_user === false) {
                supabase
                  .from('chat_messages')
                  .update({ read_by_user: true })
                  .eq('id', newMsg.id)
                  .then();
              }
            } else {
              // Widget is NOT visible — track as unread + flash notification
              setGuestUnreadCount(prev => prev + 1);
              setGuestLatestMsg(newMsg);
              setNewMsgFlash(true);
              setTimeout(() => setNewMsgFlash(false), 4000);
            }

            // Auto-translate incoming admin text messages into the user's language
            if (userLang && newMsg.type === 'text') {
              try {
                const result = await translateMessage(newMsg.message, userLang);
                if (result?.translatedText) {
                  setTranslations(prev => ({ ...prev, [newMsg.id]: { text: result.translatedText, loading: false, auto: true } }));
                }
              } catch { /* silent */ }
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatSession?.id, userLang]);

  // ── Auto-translate existing admin messages when user language is detected ──
  useEffect(() => {
    if (!userLang || messages.length === 0) return;

    const adminTextMsgs = messages.filter(
      m => m.sender_role === 'admin' && m.type === 'text' && !translations[m.id]
    );

    adminTextMsgs.forEach(async (msg) => {
      try {
        const result = await translateMessage(msg.message, userLang);
        if (result?.translatedText) {
          setTranslations(prev => ({
            ...prev,
            [msg.id]: { text: result.translatedText, loading: false, auto: true }
          }));
        }
      } catch { /* silent */ }
    });
  }, [userLang]); // Only runs when userLang is first detected/changed

  // ── Presence heartbeat (Supabase Realtime Presence + DB) ──
  useEffect(() => {
    if (!chatSession?.id) return;

    const channelName = `presence:chat:${chatSession.id}`;
    const presenceChannel = supabase.channel(channelName);

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        // No-op on client side; admin consumes presence state
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({
            online_at: new Date().toISOString(),
            user_type: isAuthenticated ? 'authenticated' : 'guest',
            user_name: chatSession.user_name,
          });
        }
      });

    // Update last_seen_at in DB immediately
    supabase
      .from('chat_sessions')
      .update({ last_seen_at: new Date().toISOString() })
      .eq('id', chatSession.id)
      .then(() => {});

    // Heartbeat: update last_seen_at every 30 seconds
    const heartbeatInterval = setInterval(() => {
      supabase
        .from('chat_sessions')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('id', chatSession.id)
        .then(() => {});
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      presenceChannel.untrack();
      supabase.removeChannel(presenceChannel);
    };
  }, [chatSession?.id, isAuthenticated]);

  // Load / refresh messages when chat is opened
  useEffect(() => {
    if (!isOpen) return;

    if (isAuthenticated && !chatSession) {
      // First time: create or load session
      loadChatSession();
    } else if (isAuthenticated && chatSession) {
      // Reopened: refresh messages from DB to catch anything missed while closed
      (async () => {
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', chatSession.id)
          .order('created_at', { ascending: true });
        if (msgs) setMessages(msgs);
      })();
      markAllRead();
    }

    if (!isAuthenticated && chatSession) {
      // Guest: refresh messages from DB
      loadGuestMessages(chatSession);
    }
  }, [isOpen]);

  // ── Polling fallback: check DB for new messages every 5s ──
  // Catches anything the realtime subscription might miss
  useEffect(() => {
    if (!chatSession?.id) return;

    const poll = async () => {
      try {
        const { data: freshMsgs } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', chatSession.id)
          .order('created_at', { ascending: true });

        if (!freshMsgs) return;

        setMessages(prev => {
          // Only update if there are new messages the state doesn't have
          if (freshMsgs.length <= prev.length) {
            // Check if last IDs match — if they do, nothing new
            const lastFresh = freshMsgs[freshMsgs.length - 1];
            const lastPrev = prev[prev.length - 1];
            if (lastFresh?.id === lastPrev?.id) return prev;
          }

          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = freshMsgs.filter(m => !existingIds.has(m.id));

          if (newMsgs.length === 0) return prev;

          // Process new admin messages for unread + auto-translate
          newMsgs.forEach(newMsg => {
            if (newMsg.sender_role === 'admin' || newMsg.sender_role === 'system') {
              if (!isVisibleRef.current) {
                setGuestUnreadCount(c => c + 1);
                setGuestLatestMsg(newMsg);
                setNewMsgFlash(true);
                setTimeout(() => setNewMsgFlash(false), 4000);
              } else if (newMsg.read_by_user === false) {
                supabase
                  .from('chat_messages')
                  .update({ read_by_user: true })
                  .eq('id', newMsg.id)
                  .then();
              }

              // Auto-translate
              if (userLang && newMsg.type === 'text') {
                translateMessage(newMsg.message, userLang).then(result => {
                  if (result?.translatedText) {
                    setTranslations(t => ({ ...t, [newMsg.id]: { text: result.translatedText, loading: false, auto: true } }));
                  }
                }).catch(() => {});
              }
            }
          });

          return freshMsgs;
        });
      } catch { /* silent */ }
    };

    const intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [chatSession?.id, userLang]);

  // ── Handle opening the chat ─────────────────────────────
  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    isVisibleRef.current = true;
    setNewMsgFlash(false);
    setGuestUnreadCount(0);
    setGuestLatestMsg(null);

    if (isAuthenticated) {
      markAllRead();
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      // Guest: show form if no existing session
      const stored = sessionStorage.getItem(GUEST_SESSION_KEY);
      if (!stored) {
        setShowGuestForm(true);
      } else {
        setShowGuestForm(false);
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    isVisibleRef.current = false;
    setShowGuestForm(false);
  };

  const handleMinimize = () => {
    const willMinimize = !isMinimized;
    setIsMinimized(willMinimize);
    isVisibleRef.current = !willMinimize;
    // If minimizing, treat it like closing for unread purposes
    // If expanding, clear unread
    if (!willMinimize) {
      setGuestUnreadCount(0);
      setGuestLatestMsg(null);
      setNewMsgFlash(false);
    }
  };

  // ── Guest pre-chat form submission ──────────────────────
  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestContact.trim() || !guestMessage.trim()) {
      setGuestFormError('Please fill in all fields.');
      return;
    }
    setGuestFormError('');
    setSubmittingGuest(true);

    try {
      // Detect IP and country
      const ip = await getVisitorIP();
      const countryCode = await getCountryFromIP(ip);

      // Determine if contact is email or phone
      const isEmail = guestContact.includes('@');
      const contactEmail = isEmail ? guestContact.trim() : null;
      const contactPhone = !isEmail ? guestContact.trim() : null;

      // Create guest session
      const { data: newSession, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: null,
          user_name: guestName.trim(),
          user_email: contactEmail,
          user_phone: contactPhone,
          is_guest: true,
          guest_ip: ip,
          country_code: countryCode,
          page_url: window.location.href,
          status: 'open',
          subject: 'Guest Support',
        })
        .select()
        .single();

      if (error) throw error;

      // Store session in sessionStorage for persistence
      sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(newSession));
      setChatSession(newSession);

      // Send welcome message from system
      const { data: welcomeMsg } = await supabase
        .from('chat_messages')
        .insert({
          session_id: newSession.id,
          sender_id: 'system',
          sender_name: 'Acebarter Support',
          sender_role: 'admin',
          message: `Hello ${guestName.trim()}! Thanks for reaching out. An agent will be with you shortly.`,
          type: 'text',
        })
        .select()
        .single();

      // Send the guest's initial message
      const { data: firstMsg } = await supabase
        .from('chat_messages')
        .insert({
          session_id: newSession.id,
          sender_id: `guest-${newSession.id}`,
          sender_name: guestName.trim(),
          sender_role: 'user',
          message: guestMessage.trim(),
          type: 'text',
        })
        .select()
        .single();

      const msgs = [];
      if (welcomeMsg) msgs.push(welcomeMsg);
      if (firstMsg) msgs.push(firstMsg);
      setMessages(msgs);

      setShowGuestForm(false);
      setGuestName('');
      setGuestContact('');
      setGuestMessage('');
      setTimeout(() => inputRef.current?.focus(), 300);
    } catch (err) {
      console.error('Guest session error:', err);
      setGuestFormError('Something went wrong. Please try again.');
    } finally {
      setSubmittingGuest(false);
    }
  };

  // ── Send message (works for both auth + guest) ──────────
  const handleSend = async () => {
    if (!inputValue.trim() || !chatSession?.id || sending) return;

    const text = inputValue.trim();
    setInputValue('');
    setSending(true);

    const senderId = isAuthenticated
      ? user.id
      : `guest-${chatSession.id}`;
    const senderName = isAuthenticated
      ? (user.name || user.email)
      : chatSession.user_name;

    const optimistic = {
      id: `temp-${Date.now()}`,
      session_id: chatSession.id,
      sender_id: senderId,
      sender_name: senderName,
      sender_role: 'user',
      message: text,
      type: 'text',
      created_at: new Date().toISOString(),
      _optimistic: true,
    };

    setMessages(prev => [...prev, optimistic]);

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          session_id: chatSession.id,
          sender_id: senderId,
          sender_name: senderName,
          sender_role: 'user',
          message: text,
          type: 'text',
        })
        .select()
        .single();

      if (error) throw error;

      setMessages(prev =>
        prev.map(m => (m.id === optimistic.id ? data : m))
      );

      setTyping(true);
      setTimeout(() => setTyping(false), 2000 + Math.random() * 3000);

      // Detect user's language from their first substantial message (runs in background)
      if (!userLang && text.length > 10) {
        detectLanguage(text).then(lang => {
          if (lang && lang !== 'en') setUserLang(lang);
        });
      }
    } catch (err) {
      console.error('Send message error:', err);
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInputValue(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Attachment file selection ─────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be under 5MB.');
      return;
    }

    // Validate type
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      alert('Only images (JPEG, PNG, GIF, WebP) and PDF files are allowed.');
      return;
    }

    setAttachmentFile(file);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachmentPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null); // PDF – no image preview
    }
  };

  // ── Translate handler ──────────────────────────────────
  // For admin messages: translate into user's detected language (if known), otherwise auto→en
  // For user's own messages: translate to English
  const handleTranslate = async (msgId, text, senderRole) => {
    setTranslations(prev => ({ ...prev, [msgId]: { text: null, loading: true } }));
    try {
      const targetLang = (senderRole === 'admin' && userLang) ? userLang : 'en';
      const result = await translateMessage(text, targetLang);
      const translated = result ? result.translatedText : null;

      // If we got a detected language back and haven't set userLang yet, capture it
      if (!userLang && result?.detectedLang && result.detectedLang !== 'en' && senderRole === 'user') {
        setUserLang(result.detectedLang);
      }

      setTranslations(prev => ({ ...prev, [msgId]: { text: translated, loading: false } }));
    } catch {
      setTranslations(prev => ({ ...prev, [msgId]: { text: null, loading: false } }));
    }
  };

  const clearAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Upload attachment & send as message ───────────────
  const handleSendAttachment = async () => {
    if (!attachmentFile || !chatSession?.id || uploading) return;

    setUploading(true);

    const senderId = isAuthenticated
      ? user.id
      : `guest-${chatSession.id}`;
    const senderName = isAuthenticated
      ? (user.name || user.email)
      : chatSession.user_name;

    const isImage = attachmentFile.type.startsWith('image/');
    const ext = attachmentFile.name.split('.').pop();
    const filePath = `${chatSession.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

    try {
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('chat-attachments')
        .upload(filePath, attachmentFile, { upsert: false });

      if (uploadError) throw uploadError;

      // Get signed URL (expires in 24h)
      const { data: urlData, error: urlError } = await supabase.storage
        .from('chat-attachments')
        .createSignedUrl(filePath, 86400);

      if (urlError) throw urlError;
      const publicUrl = urlData.signedUrl;

      // Insert message
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          session_id: chatSession.id,
          sender_id: senderId,
          sender_name: senderName,
          sender_role: 'user',
          message: publicUrl,
          type: isImage ? 'image' : 'file',
        })
        .select()
        .single();

      if (error) throw error;

      setMessages(prev => [...prev, data]);
      clearAttachment();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  return (
    <>
      {/* ── Floating Chat Button (always visible when chat is closed) ── */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className={`fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-[60] w-14 h-14 bg-accent hover:bg-accent/90 text-white rounded-full shadow-lg shadow-accent/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95 group ${
            newMsgFlash ? 'animate-bounce ring-4 ring-accent/40' : ''
          }`}
          aria-label="Open Help Center"
        >
          <Icon name="MessageCircle" size={24} />

          {/* Unread badge (works for both guests and authenticated users) */}
          {(unreadCount > 0 || guestUnreadCount > 0) && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] flex items-center justify-center bg-error text-white text-[10px] font-bold rounded-full border-2 border-background shadow-lg animate-pulse">
              {(() => { const total = (unreadCount || 0) + guestUnreadCount; return total > 9 ? '9+' : total; })()}
            </span>
          )}

          {/* Tooltip */}
          {(() => {
            const total = (unreadCount || 0) + guestUnreadCount;
            return (
              <span className="absolute right-full mr-3 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                {total > 0 ? `${total} new message${total > 1 ? 's' : ''}` : t('chat.helpCenter')}
              </span>
            );
          })()}
        </button>
      )}

      {/* ── New message toast (shows briefly when chat is closed) ── */}
      {!isOpen && newMsgFlash && (latestMessage || guestLatestMsg) && (
        <div
          onClick={handleOpen}
          className="fixed bottom-36 lg:bottom-[88px] right-4 lg:right-6 z-[60] w-72 bg-card border border-border rounded-2xl shadow-2xl p-3 cursor-pointer animate-in slide-in-from-bottom-2 fade-in duration-300 hover:border-accent/30 transition-colors"
        >
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Headphones" size={14} className="text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-accent">Acebarter Support</p>
              <p className="text-xs text-foreground mt-0.5 line-clamp-2">
                {(latestMessage || guestLatestMsg)?.message}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">Tap to open chat</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Chat Window ── */}
      {isOpen && (
        <div className={`fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-[60] w-[360px] max-w-[calc(100vw-2rem)] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${
          isMinimized ? 'h-[56px]' : 'max-h-[min(520px,calc(100dvh-6rem))]'
        }`}
        style={!isMinimized ? { height: 'min(520px, calc(100dvh - 6rem))' } : undefined}>
          {/* Header */}
          <div className="bg-accent text-white px-4 py-3 flex items-center gap-3 flex-shrink-0 cursor-pointer" onClick={handleMinimize}>
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Icon name="Headphones" size={18} />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success border-2 border-accent rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">{t('chat.helpCenter')}</p>
              <p className="text-[10px] text-white/70 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-success rounded-full inline-block" />
                {t('chat.online')}
              </p>
            </div>
            {/* Unread badge when minimized */}
            {isMinimized && ((unreadCount || 0) + guestUnreadCount) > 0 && (
              <span className="min-w-[22px] h-[22px] flex items-center justify-center bg-error text-white text-[10px] font-bold rounded-full animate-pulse">
                {(() => { const t = (unreadCount || 0) + guestUnreadCount; return t > 9 ? '9+' : t; })()}
              </span>
            )}
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                <Icon name={isMinimized ? 'ChevronUp' : 'Minus'} size={16} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Close chat"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>

          {/* ── Guest Pre-Chat Form ── */}
          {!isMinimized && showGuestForm && !isAuthenticated && (
            <div className="flex-1 overflow-y-auto p-5 bg-background/50">
              <div className="text-center mb-5">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                  <Icon name="MessageSquare" size={24} className="text-accent" />
                </div>
                <h3 className="text-sm font-bold text-foreground">{t('chat.startConversation')}</h3>
                <p className="text-xs text-muted-foreground mt-1">{t('chat.tellUs')}</p>
              </div>

              <form onSubmit={handleGuestSubmit} className="space-y-3">
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="mt-1 w-full px-3.5 py-2.5 bg-background-subtle border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Email or Phone *
                  </label>
                  <input
                    type="text"
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder="you@email.com or +1234567890"
                    required
                    className="mt-1 w-full px-3.5 py-2.5 bg-background-subtle border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    How can we help? *
                  </label>
                  <textarea
                    value={guestMessage}
                    onChange={(e) => setGuestMessage(e.target.value)}
                    placeholder="Describe your question or issue..."
                    required
                    rows={3}
                    className="mt-1 w-full px-3.5 py-2.5 bg-background-subtle border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                  />
                </div>

                {guestFormError && (
                  <p className="text-xs text-error flex items-center gap-1.5">
                    <Icon name="AlertCircle" size={12} />
                    {guestFormError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submittingGuest}
                  className="w-full bg-accent hover:bg-accent/90 text-white py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingGuest ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      Starting chat...
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={16} />
                      Start Chat
                    </>
                  )}
                </button>
              </form>

              <p className="text-[9px] text-muted-foreground/50 text-center mt-4">
                Powered by Acebarter &middot; Your info helps us assist you
              </p>
            </div>
          )}

          {/* ── Messages Area (shown after form is submitted or for auth users) ── */}
          {!isMinimized && !showGuestForm && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/50 scrollbar-premium">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Icon name="Loader2" size={28} className="animate-spin text-accent mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">Loading chat...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {Object.entries(groupedMessages).map(([date, msgs]) => (
                      <div key={date}>
                        <div className="flex items-center gap-3 my-3">
                          <div className="flex-1 h-px bg-border" />
                          <span className="text-[10px] font-medium text-muted-foreground bg-background-subtle px-2 py-0.5 rounded-full">{date}</span>
                          <div className="flex-1 h-px bg-border" />
                        </div>

                        {msgs.map((msg) => {
                          const isUser = msg.sender_role === 'user';

                          return (
                            <div
                              key={msg.id}
                              className={`flex gap-2 mb-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
                            >
                              {!isUser && (
                                <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-1">
                                  <Icon name="Headphones" size={14} className="text-accent" />
                                </div>
                              )}

                              <div className={`max-w-[75%] ${isUser ? 'items-end' : 'items-start'}`}>
                                {msg.type === 'image' ? (
                                  <a
                                    href={msg.message}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block rounded-2xl overflow-hidden border ${
                                      isUser
                                        ? 'border-accent/30 rounded-br-md'
                                        : 'border-border rounded-bl-md'
                                    } ${msg._optimistic ? 'opacity-70' : ''}`}
                                  >
                                    <img
                                      src={msg.message}
                                      alt="Shared image"
                                      className="max-w-full max-h-52 object-cover"
                                      loading="lazy"
                                    />
                                  </a>
                                ) : msg.type === 'file' ? (
                                  <a
                                    href={msg.message}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 px-3.5 py-2.5 rounded-2xl text-sm ${
                                      isUser
                                        ? 'bg-accent text-white rounded-br-md hover:bg-accent/90'
                                        : 'bg-card border border-border text-foreground rounded-bl-md hover:bg-card-hover'
                                    } ${msg._optimistic ? 'opacity-70' : ''} transition-colors`}
                                  >
                                    <Icon name="FileText" size={16} className="flex-shrink-0" />
                                    <span>{t('chat.viewAttachment')}</span>
                                    <Icon name="ExternalLink" size={12} className="flex-shrink-0 opacity-60" />
                                  </a>
                                ) : (
                                <div>
                                  {/* Auto-translated admin messages: show translated text in the bubble */}
                                  {translations[msg.id]?.auto && translations[msg.id]?.text ? (
                                    <>
                                      <div
                                        className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                          isUser
                                            ? 'bg-accent text-white rounded-br-md'
                                            : 'bg-card border border-border text-foreground rounded-bl-md'
                                        } ${msg._optimistic ? 'opacity-70' : ''}`}
                                      >
                                        {translations[msg.id].text}
                                      </div>
                                      {/* Show original toggle */}
                                      {translations[msg.id]._showOriginal && (
                                        <div className={`mt-1 px-3 py-1.5 bg-background-subtle border border-border/50 rounded-xl text-xs text-muted-foreground leading-relaxed ${!isUser ? 'text-left' : 'text-right'}`}>
                                          <div className="flex items-center gap-1 text-[9px] text-muted-foreground/60 mb-0.5">
                                            <Icon name="Languages" size={9} />
                                            Original
                                          </div>
                                          {msg.message}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div
                                      className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                        isUser
                                          ? 'bg-accent text-white rounded-br-md'
                                          : 'bg-card border border-border text-foreground rounded-bl-md'
                                      } ${msg._optimistic ? 'opacity-70' : ''}`}
                                    >
                                      {msg.message}
                                    </div>
                                  )}
                                </div>
                                )}
                                <p className={`text-[9px] text-muted-foreground/60 mt-1 px-1 ${isUser ? 'text-right' : 'text-left'}`}>
                                  {formatTime(msg.created_at)}
                                  {isUser && msg._optimistic && ' · Sending...'}
                                  {isUser && !msg._optimistic && (
                                    <span className="ml-1">
                                      <Icon name="CheckCheck" size={10} className="inline text-accent/60" />
                                    </span>
                                  )}
                                </p>

                                {/* Translate / Show original controls */}
                                {msg.type !== 'image' && msg.type !== 'file' && msg.type !== 'system' && (
                                  <div className={`mt-1 px-1 ${isUser ? 'text-right' : 'text-left'}`}>
                                    {translations[msg.id]?.loading ? (
                                      <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                        <Icon name="Loader2" size={10} className="animate-spin" />
                                        Translating...
                                      </span>
                                    ) : translations[msg.id]?.auto && translations[msg.id]?.text ? (
                                      /* Auto-translated: toggle show/hide original */
                                      <button
                                        onClick={() => setTranslations(prev => ({
                                          ...prev,
                                          [msg.id]: { ...prev[msg.id], _showOriginal: !prev[msg.id]._showOriginal }
                                        }))}
                                        className="text-[10px] text-info/60 hover:text-info inline-flex items-center gap-1 transition-colors"
                                      >
                                        <Icon name="Languages" size={10} />
                                        {translations[msg.id]._showOriginal ? 'Hide original' : 'Show original'}
                                      </button>
                                    ) : translations[msg.id] && translations[msg.id].text && !translations[msg.id].auto ? (
                                      /* Manual translate result */
                                      <div className={`mt-1 px-3 py-1.5 bg-info-muted/50 border border-info/10 rounded-xl text-xs text-foreground leading-relaxed text-left`}>
                                        <div className="flex items-center justify-between gap-2 mb-0.5">
                                          <span className="flex items-center gap-1 text-[9px] text-info font-medium">
                                            <Icon name="Languages" size={10} />
                                            Translated
                                          </span>
                                          <button
                                            onClick={() => setTranslations(prev => { const n = { ...prev }; delete n[msg.id]; return n; })}
                                            className="text-[9px] text-muted-foreground hover:text-foreground transition-colors"
                                          >
                                            Hide
                                          </button>
                                        </div>
                                        {translations[msg.id].text}
                                      </div>
                                    ) : translations[msg.id] && translations[msg.id].text === null && !translations[msg.id].loading ? (
                                      <span className="text-[10px] text-muted-foreground/50 inline-flex items-center gap-1">
                                        <Icon name="Languages" size={10} />
                                        No translation needed
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleTranslate(msg.id, msg.message, msg.sender_role)}
                                        className="text-[10px] text-accent/60 hover:text-accent inline-flex items-center gap-1 transition-colors"
                                      >
                                        <Icon name="Languages" size={10} />
                                        Translate
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {typing && (
                      <div className="flex gap-2 items-end">
                        <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="Headphones" size={14} className="text-accent" />
                        </div>
                        <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {messages.length <= 1 && !loading && isAuthenticated && (
                <div className="px-4 pb-2 flex gap-2 flex-wrap">
                  {['How do I deposit?', 'Withdrawal status', 'KYC help', 'Investment info'].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInputValue(q); inputRef.current?.focus(); }}
                      className="px-3 py-1.5 text-[11px] font-medium bg-accent/10 text-accent border border-accent/20 rounded-full hover:bg-accent/20 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              <div className="border-t border-border bg-card flex-shrink-0">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  className="hidden"
                />

                {/* Attachment preview bar */}
                {attachmentFile && (
                  <div className="px-2.5 pt-2 flex items-center gap-2">
                    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-accent/10 border border-accent/20 rounded-xl flex-1 min-w-0">
                      {attachmentPreview ? (
                        <img src={attachmentPreview} alt="Preview" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                          <Icon name="FileText" size={18} className="text-accent" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{attachmentFile.name}</p>
                        <p className="text-[10px] text-muted-foreground">{(attachmentFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button
                        onClick={clearAttachment}
                        className="p-1 hover:bg-error-muted rounded-md text-muted-foreground hover:text-error transition-colors flex-shrink-0"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                    <button
                      onClick={handleSendAttachment}
                      disabled={uploading}
                      className="p-2 bg-accent hover:bg-accent/90 text-white rounded-xl transition-all disabled:opacity-40 flex-shrink-0 active:scale-95"
                    >
                      {uploading ? (
                        <Icon name="Loader2" size={18} className="animate-spin" />
                      ) : (
                        <Icon name="Send" size={18} />
                      )}
                    </button>
                  </div>
                )}

                {/* Input row */}
                <div className="p-2.5 flex items-end gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-2 text-muted-foreground hover:text-accent hover:bg-accent/10 rounded-xl transition-colors flex-shrink-0 disabled:opacity-40"
                    title="Attach image or file"
                  >
                    <Icon name="Paperclip" size={18} />
                  </button>
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 min-w-0 resize-none bg-background-subtle border border-border rounded-xl px-3.5 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/30 max-h-20 overflow-y-auto"
                    style={{ minHeight: '38px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!inputValue.trim() || sending}
                    className="p-2 bg-accent hover:bg-accent/90 text-white rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 active:scale-95"
                  >
                    {sending ? (
                      <Icon name="Loader2" size={18} className="animate-spin" />
                    ) : (
                      <Icon name="Send" size={18} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LiveChat;
